// src/lib/supabase/queries/project-queries.ts
/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 5, "notes": "Project CRUD and mapping", "tuVersion": "2.5"}
 * .help "Project queries and row mapping; includes active_project_estimate_id for Resources accepted bid."
 */
import { supabase } from '../../supabase';
import { Project, ProjectStatus } from '../../../types';

// Define types manually until we regenerate Supabase types
type ProjectRow = {
  id: string;
  name: string;
  description: string | null;
  project_key: string;
  status: string;
  company_id: string;
  team_id: string | null;
  roadmap_id: string | null;
  start_date: string | null;
  target_end_date: string | null;
  actual_end_date: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  active_project_estimate_id?: string | null;
};

type ProjectInsert = {
  name?: string;
  description?: string | null;
  project_key?: string;
  status?: string;
  company_id?: string;
  team_id?: string | null;
  roadmap_id?: string | null;
  start_date?: string | null;
  target_end_date?: string | null;
  actual_end_date?: string | null;
  created_by?: string | null;
};

type ProjectUpdate = {
  name?: string;
  description?: string | null;
  project_key?: string;
  status?: string;
  team_id?: string | null;
  roadmap_id?: string | null;
  start_date?: string | null;
  target_end_date?: string | null;
  actual_end_date?: string | null;
};

// ============================================
// MAPPERS (DB <-> App Types)
// ============================================

export const mapProjectFromDB = (row: ProjectRow): Project => ({
  id: row.id,
  name: row.name || '',
  description: row.description || '',
  projectKey: row.project_key || '',
  status: (row.status as ProjectStatus) || 'planning',
  companyId: row.company_id,
  teamId: row.team_id || undefined,
  roadmapId: row.roadmap_id || undefined,
  startDate: row.start_date || undefined,
  targetEndDate: row.target_end_date || undefined,
  actualEndDate: row.actual_end_date || undefined,
  createdBy: row.created_by || undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  activeProjectEstimateId: row.active_project_estimate_id ?? undefined,
});

export const mapProjectToDB = (project: Partial<Project>): ProjectInsert => ({
  name: project.name,
  description: project.description,
  project_key: project.projectKey,
  status: project.status,
  company_id: project.companyId,
  team_id: project.teamId || null,
  roadmap_id: project.roadmapId || null,
  start_date: project.startDate || null,
  target_end_date: project.targetEndDate || null,
  actual_end_date: project.actualEndDate || null,
  created_by: project.createdBy || null,
});

export const mapProjectUpdatesToDB = (updates: Partial<Project>): ProjectUpdate => {
  const dbUpdates: ProjectUpdate = {};
  
  // Only include fields that are actually being updated (not undefined)
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.projectKey !== undefined) dbUpdates.project_key = updates.projectKey;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.teamId !== undefined) dbUpdates.team_id = updates.teamId || null;
  if (updates.roadmapId !== undefined) dbUpdates.roadmap_id = updates.roadmapId || null;
  if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate || null;
  if (updates.targetEndDate !== undefined) dbUpdates.target_end_date = updates.targetEndDate || null;
  if (updates.actualEndDate !== undefined) dbUpdates.actual_end_date = updates.actualEndDate || null;
  
  return dbUpdates;
};

// ============================================
// QUERY FUNCTIONS
// ============================================

/**
 * Fetch all projects for a company, optionally filtered by team or roadmap
 */
export const fetchProjects = async (
  companyId: string,
  options?: {
    teamId?: string | null;
    roadmapId?: string | null;
    status?: ProjectStatus;
  }
): Promise<Project[]> => {
  let query = supabase
    .from('projects')
    .select('*')
    .eq('company_id', companyId);

  if (options?.teamId !== undefined) {
    if (options.teamId === null) {
      query = query.is('team_id', null);
    } else {
      query = query.eq('team_id', options.teamId);
    }
  }

  if (options?.roadmapId !== undefined) {
    if (options.roadmapId === null) {
      query = query.is('roadmap_id', null);
    } else {
      query = query.eq('roadmap_id', options.roadmapId);
    }
  }

  if (options?.status) {
    query = query.eq('status', options.status);
  }

  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map(mapProjectFromDB);
};

/**
 * Fetch a single project by ID
 */
export const fetchProjectById = async (projectId: string): Promise<Project | null> => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data ? mapProjectFromDB(data) : null;
};

/**
 * Fetch project stats (cards, sprints, requirements counts)
 */
export const fetchProjectStats = async (projectId: string) => {
  const { data, error } = await supabase
    .rpc('get_project_stats', { p_project_id: projectId });

  if (error) throw error;

  return data || {
    totalCards: 0,
    completedCards: 0,
    activeCards: 0,
    totalSprints: 0,
    activeSprints: 0,
    totalRequirements: 0,
    totalPlans: 0,
  };
};

/**
 * Fetch project progress percentage (0-100)
 */
export const fetchProjectProgress = async (projectId: string): Promise<number> => {
  const { data, error } = await supabase
    .rpc('get_project_progress', { p_project_id: projectId });

  if (error) throw error;

  return data || 0;
};

/**
 * Create a new project
 * November 3, 2025: Added sprintIds and featureIds parameters to handle associations
 */
export const insertProject = async (
  projectData: Partial<Project>,
  sprintIds?: string[],
  featureIds?: string[]
): Promise<Project> => {
  const dbData = mapProjectToDB(projectData);

  const { data: projectResult, error: projectError } = await supabase
    .from('projects')
    .insert(dbData)
    .select()
    .single();

  if (projectError) throw projectError;
  if (!projectResult) throw new Error('Failed to create project');

  const newProjectId = projectResult.id;
  const companyId = projectResult.company_id;

  // November 3, 2025: Handle Sprint associations for new project
  if (sprintIds && sprintIds.length > 0) {
    const { error: linkSprintsError } = await supabase
      .from('sprints')
      .update({ project_id: newProjectId })
      .in('id', sprintIds)
      .eq('company_id', companyId);
    if (linkSprintsError) throw linkSprintsError;
  }

  // November 3, 2025: Handle Feature (Requirement) associations for new project
  if (featureIds && featureIds.length > 0) {
    const { error: linkFeaturesError } = await supabase
      .from('requirements')
      .update({ project_id: newProjectId })
      .in('id', featureIds)
      .eq('company_id', companyId);
    if (linkFeaturesError) throw linkFeaturesError;
  }

  return mapProjectFromDB(projectResult);
};

/**
 * Update an existing project
 * November 3, 2025: Added sprintIds and featureIds parameters to handle associations
 */
export const updateProjectById = async (
  projectId: string,
  updates: Partial<Project>,
  sprintIds?: string[],
  featureIds?: string[],
  _cardIds?: string[]
): Promise<Project> => {
  const dbUpdates = mapProjectUpdatesToDB(updates);

  // Only update the project table if there are actual field updates
  // If only associations are being updated (sprintIds/featureIds), skip the project update
  let projectData;
  if (Object.keys(dbUpdates).length > 0) {
    const { data, error: projectError } = await supabase
      .from('projects')
      .update(dbUpdates)
      .eq('id', projectId)
      .select()
      .single();

    if (projectError) throw projectError;
    if (!data) throw new Error('Failed to update project');
    projectData = data;
  } else {
    // If no project fields to update, fetch the current project data
    const { data, error: projectError } = await supabase
      .from('projects')
      .select()
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;
    if (!data) throw new Error('Project not found');
    projectData = data;
  }

  const companyId = projectData.company_id;

  // November 3, 2025: Handle Sprint associations
  if (sprintIds !== undefined) {
    // Get current sprints associated with this project
    const { data: currentSprints, error: currentSprintsError } = await supabase
      .from('sprints')
      .select('id')
      .eq('project_id', projectId)
      .eq('company_id', companyId);

    if (currentSprintsError) throw currentSprintsError;

    const currentSprintIds = new Set((currentSprints || []).map(s => s.id));
    
    // Unlink sprints that are no longer selected
    const sprintsToUnlink = [...currentSprintIds].filter(id => !sprintIds.includes(id));
    if (sprintsToUnlink.length > 0) {
      const { error: unlinkError } = await supabase
        .from('sprints')
        .update({ project_id: null })
        .in('id', sprintsToUnlink)
        .eq('company_id', companyId);
      if (unlinkError) throw unlinkError;
    }

    // Link new sprints
    const sprintsToLink = sprintIds.filter(id => !currentSprintIds.has(id));
    if (sprintsToLink.length > 0) {
      const { error: linkError } = await supabase
        .from('sprints')
        .update({ project_id: projectId })
        .in('id', sprintsToLink)
        .eq('company_id', companyId);
      if (linkError) throw linkError;
    }
  }

  // November 3, 2025: Handle Feature (Requirement) associations
  if (featureIds !== undefined) {
    // Get current features associated with this project
    const { data: currentFeatures, error: currentFeaturesError } = await supabase
      .from('requirements')
      .select('id')
      .eq('project_id', projectId)
      .eq('company_id', companyId);

    if (currentFeaturesError) throw currentFeaturesError;

    const currentFeatureIds = new Set((currentFeatures || []).map(r => r.id));
    
    // Unlink features that are no longer selected
    const featuresToUnlink = [...currentFeatureIds].filter(id => !featureIds.includes(id));
    if (featuresToUnlink.length > 0) {
      const { error: unlinkError } = await supabase
        .from('requirements')
        .update({ project_id: null })
        .in('id', featuresToUnlink)
        .eq('company_id', companyId);
      if (unlinkError) throw unlinkError;
    }

    // Link new features
    const featuresToLink = featureIds.filter(id => !currentFeatureIds.has(id));
    if (featuresToLink.length > 0) {
      const { error: linkError } = await supabase
        .from('requirements')
        .update({ project_id: projectId })
        .in('id', featuresToLink)
        .eq('company_id', companyId);
      if (linkError) throw linkError;
    }
  }

  return mapProjectFromDB(projectData);
};

/**
 * Delete a project
 */
export const deleteProjectById = async (projectId: string): Promise<void> => {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', projectId);

  if (error) throw error;
};

/**
 * Fetch projects with full stats (enriched data)
 */
export const fetchProjectsWithStats = async (
  companyId: string,
  options?: {
    teamId?: string | null;
    roadmapId?: string | null;
    status?: ProjectStatus;
  }
): Promise<Project[]> => {
  const projects = await fetchProjects(companyId, options);

  // Fetch stats for all projects in parallel
  const projectsWithStats = await Promise.all(
    projects.map(async (project) => {
      try {
        const [stats, progress] = await Promise.all([
          fetchProjectStats(project.id),
          fetchProjectProgress(project.id),
        ]);

        return {
          ...project,
          stats,
          progress,
        };
      } catch (err) {
        console.error(`Failed to fetch stats for project ${project.id}:`, err);
        return project; // Return project without stats if fetch fails
      }
    })
  );

  return projectsWithStats;
};

/**
 * Check if a project key is unique within a company
 */
export const isProjectKeyUnique = async (
  companyId: string,
  projectKey: string,
  excludeProjectId?: string
): Promise<boolean> => {
  let query = supabase
    .from('projects')
    .select('id')
    .eq('company_id', companyId)
    .eq('project_key', projectKey);

  if (excludeProjectId) {
    query = query.neq('id', excludeProjectId);
  }

  const { data, error } = await query;

  if (error) throw error;

  return !data || data.length === 0;
};

// ============================================
// PROJECT ACTIVITY TYPES (live activity types per project; part number = project_key + '-' + code)
// ============================================

export type ProjectActivityTypeRow = {
  id: string;
  company_id: string;
  project_id: string;
  code: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ProjectActivityType = {
  id: string;
  companyId: string;
  projectId: string;
  code: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

const mapProjectActivityTypeFromDB = (row: ProjectActivityTypeRow): ProjectActivityType => ({
  id: row.id,
  companyId: row.company_id,
  projectId: row.project_id,
  code: row.code,
  name: row.name,
  sortOrder: row.sort_order ?? 0,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

/** ActivityType (from liveActivityService) -> default code and name for project_activity_types */
export const ACTIVITY_TYPE_TO_CODE_AND_NAME: Record<string, { code: string; name: string }> = {
  flow: { code: 'FLOW', name: 'Flow Mode' },
  driving: { code: 'DRV', name: 'Driving' },
  lunch: { code: 'LUNCH', name: 'Meal Break' },
  vacation: { code: 'VAC', name: 'Vacation' },
  holiday: { code: 'HOL', name: 'Holiday' },
  ooo: { code: 'OOO', name: 'Out of Office' },
  meeting: { code: 'MTG', name: 'In a Meeting' },
  break: { code: 'BRK', name: 'Quick Break' },
  custom: { code: 'CUSTOM', name: 'Custom' },
};

export const fetchProjectActivityTypes = async (projectId: string): Promise<ProjectActivityType[]> => {
  const { data, error } = await supabase
    .from('project_activity_types')
    .select('*')
    .eq('project_id', projectId)
    .order('sort_order', { ascending: true })
    .order('code', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(mapProjectActivityTypeFromDB);
};

/** Get or create a project activity type for the given activity type (e.g. 'driving' -> DRV / Driving). */
export const getOrCreateProjectActivityType = async (
  companyId: string,
  projectId: string,
  activityType: string,
  customName?: string
): Promise<ProjectActivityType> => {
  const { code, name } = ACTIVITY_TYPE_TO_CODE_AND_NAME[activityType] ?? {
    code: activityType.toUpperCase().slice(0, 8),
    name: customName || activityType,
  };
  const existing = await supabase
    .from('project_activity_types')
    .select('*')
    .eq('project_id', projectId)
    .eq('code', code)
    .maybeSingle();

  if (existing.data) {
    return mapProjectActivityTypeFromDB(existing.data as ProjectActivityTypeRow);
  }

  const { data: inserted, error } = await supabase
    .from('project_activity_types')
    .insert({
      company_id: companyId,
      project_id: projectId,
      code,
      name: customName || name,
      sort_order: 0,
    })
    .select()
    .single();

  if (error) throw error;
  return mapProjectActivityTypeFromDB(inserted as ProjectActivityTypeRow);
};

